import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import AppShell from "@/components/AppShell";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Check } from "lucide-react";

export default function Billing() {
  const { plan, refresh } = useAuth();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(null);
  const [sp, setSp] = useSearchParams();

  useEffect(() => {
    api.get("/plans").then((r) => setPlans(r.data.plans || []));
  }, []);

  // handle mock success redirect
  useEffect(() => {
    const mock = sp.get("mock_success");
    if (mock) {
      api.post("/billing/mock-activate", { plan: mock }).then(() => {
        toast.success(`Upgraded to ${mock} (mock mode)`);
        refresh();
        setSp({});
      }).catch(() => {});
    }
  }, [sp, refresh, setSp]);

  // After returning from Dodo checkout, poll /auth/me until plan upgrades (webhook is async)
  useEffect(() => {
    if (sp.get("status") !== "success") return;
    let tries = 0;
    const initialPlan = plan?.id;
    const tick = setInterval(async () => {
      tries += 1;
      await refresh();
      if (tries >= 12) {
        clearInterval(tick);
        toast.info("Payment successful — plan will update shortly. Refresh if needed.");
        setSp({});
      }
    }, 2000);
    return () => clearInterval(tick);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sp]);

  // Show toast & clear once plan changes after success redirect
  useEffect(() => {
    if (sp.get("status") === "success" && plan?.id && plan.id !== "free") {
      toast.success(`Welcome to ${plan.name} — ${plan.quota} emails/month unlocked.`);
      setSp({});
    }
  }, [plan, sp, setSp]);

  const choose = async (planId) => {
    setLoading(planId);
    try {
      const { data } = await api.post("/billing/checkout", { plan: planId });
      if (data.checkout_url) {
        if (data.mocked) toast.info("Dodo not configured — simulating checkout");
        window.location.href = data.checkout_url;
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Checkout failed");
    } finally {
      setLoading(null);
    }
  };

  return (
    <AppShell>
      <span className="label-xs">Billing</span>
      <h1 className="mt-2 text-3xl md:text-4xl font-display tracking-tighter">Choose your plan.</h1>
      <p className="mt-3 text-slate-600">Simple monthly pricing. Cancel anytime.</p>

      <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6">
        {plans.map((p) => {
          const isCurrent = plan?.id === p.id;
          const featured = p.id === "pro";
          return (
            <div key={p.id}
              className={`tech-card p-8 flex flex-col relative ${featured ? "border-[#0047FF] border-2" : ""}`}
              data-testid={`billing-plan-${p.id}`}>
              {featured && <span className="absolute -top-3 left-8 bg-[#0047FF] text-white text-xs px-2.5 py-1 rounded-full font-medium">Most popular</span>}
              {isCurrent && <span className="absolute -top-3 right-8 bg-emerald-600 text-white text-xs px-2.5 py-1 rounded-full font-medium">Current</span>}
              <div className="label-xs">{p.name}</div>
              <div className="mt-4 flex items-baseline gap-1">
                <span className="text-5xl font-display tracking-tighter">${p.price}</span>
                <span className="text-slate-500">/month</span>
              </div>
              <div className="mt-1 text-slate-600">{p.quota} emails / month</div>
              <ul className="mt-6 space-y-2 text-sm text-slate-600 flex-1">
                <li className="flex items-start gap-2"><Check className="h-4 w-4 text-[#10B981] mt-0.5" /> {p.quota} AI-generated emails</li>
                <li className="flex items-start gap-2"><Check className="h-4 w-4 text-[#10B981] mt-0.5" /> Send from your Gmail</li>
                <li className="flex items-start gap-2"><Check className="h-4 w-4 text-[#10B981] mt-0.5" /> Email history</li>
                {p.id !== "starter" && <li className="flex items-start gap-2"><Check className="h-4 w-4 text-[#10B981] mt-0.5" /> Priority support</li>}
              </ul>
              <button onClick={() => choose(p.id)} disabled={loading === p.id || isCurrent}
                data-testid={`billing-cta-${p.id}`}
                className={`mt-8 py-3 rounded-md font-medium transition-colors ${
                  isCurrent ? "bg-slate-100 text-slate-400 cursor-not-allowed" :
                  featured ? "bg-[#0047FF] text-white hover:bg-[#003BE3]" :
                  "bg-white border border-slate-200 text-slate-900 hover:bg-slate-50"
                }`}>
                {isCurrent ? "Current plan" : loading === p.id ? "Redirecting…" : `Choose ${p.name}`}
              </button>
            </div>
          );
        })}
      </div>

      <p className="mt-10 text-xs text-slate-500">
        Payments powered by Dodo Payments. Placeholder keys in use during development — production billing will be enabled once real API keys are configured.
      </p>
    </AppShell>
  );
}
